<template>
  <div class="highlights-shell">
    <div class="section-header">
      <div>
        <h3 class="text-lg font-semibold">{{ resultCountLabel }}</h3>
      </div>
    </div>

    <div class="highlights-body">
      <div
        v-if="isLoading"
        class="highlights-skeleton-shell"
      >
        <vue-slick-carousel v-bind="skeletonSettings" class="highlights-carousel">
          <div
            v-for="card in skeletonCards"
            :key="card"
            class="highlight-card highlight-card-skeleton"
            aria-hidden="true"
          >
            <div class="highlight-thumbnail-shell highlight-thumbnail-shell-skeleton">
              <div class="highlight-skeleton-block"></div>
            </div>
          </div>
        </vue-slick-carousel>
      </div>

      <div
        v-else-if="highlights.length"
        class="highlights-carousel-shell"
        :class="{ 'highlights-carousel-ready': previewsSettled }"
        @wheel.prevent="handleWheelScroll"
      >
        <vue-slick-carousel
          ref="highlightsCarousel"
          v-bind="carouselSettings"
          class="highlights-carousel"
        >
          <button
            v-for="highlight in highlights"
            :key="thumbnailKey(highlight)"
            type="button"
            class="highlight-card"
            :class="{ 'highlight-card-active': highlightEventId(highlight) === selectedEventId }"
            @mouseenter="startPreview(highlight)"
            @mouseleave="stopPreview(highlight)"
            @focus="startPreview(highlight)"
            @blur="stopPreview(highlight)"
            @click="$emit('select-highlight', highlight)"
          >
            <div class="highlight-thumbnail-shell">
              <span class="highlight-duration-badge">
                {{ highlightDurationLabel(highlight) }}
              </span>
              <video
                v-if="activeVideoUrl"
                :ref="`preview-${thumbnailKey(highlight)}`"
                class="highlight-preview"
                muted
                playsinline
                preload="metadata"
                :src="activeVideoUrl"
                @loadedmetadata="preparePreview(highlight)"
                @seeked="markPreviewReady(highlight)"
                @error="markPreviewError(highlight)"
              ></video>
              <span class="highlight-play-overlay" aria-hidden="true">
                <svg
                  class="highlight-play-icon"
                  viewBox="0 0 512 512"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="256" cy="256" r="112" fill="rgba(15, 23, 42, 0.58)" />
                  <path
                    d="M224 186L336 256L224 326V186Z"
                    fill="#FFFFFF"
                  />
                </svg>
              </span>
              <div v-if="showPreviewFallback(highlight)" class="highlight-thumbnail-fallback">
                  <span class="highlight-fallback-time">Thumbnail Unavailable</span>
              </div>
            </div>
          </button>
        </vue-slick-carousel>
      </div>
    </div>
  </div>
</template>

<script>
import VueSlickCarousel from 'vue-slick-carousel'
import 'vue-slick-carousel/dist/vue-slick-carousel.css'
import 'vue-slick-carousel/dist/vue-slick-carousel-theme.css'

export default {
  name: 'CeremonyHighlights',
  components: {
    VueSlickCarousel,
  },
  props: {
    highlights: {
      type: Array,
      default: () => [],
    },
    selectedEventId: {
      type: String,
      default: '',
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    activeVideoUrl: {
      type: String,
      default: '',
    },
    formatDuration: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      carouselSettings: {
        arrows: true,
        dots: false,
        draggable: true,
        infinite: false,
        slidesToShow: 4,
        slidesToScroll: 1,
        speed: 250,
        swipe: true,
        swipeToSlide: true,
        touchMove: true,
        responsive: [
          {
            breakpoint: 1280,
            settings: {
              slidesToShow: 3,
            },
          },
          {
            breakpoint: 900,
            settings: {
              slidesToShow: 2,
            },
          },
          {
            breakpoint: 640,
            settings: {
              slidesToShow: 1,
            },
          },
        ],
      },
      lastWheelDirection: 0,
      previewReadyMap: {},
      previewErrorMap: {},
      previewTimers: {},
      wheelResetTimer: null,
    }
  },
  computed: {
    resultCountLabel() {
      if (this.isLoading) return 'Loading Highlights'
      const count = this.highlights.length
      if (count === 0) return 'No Highlight Found'
      if (count === 1) return '1 Highlight Found'
      return `${count} Highlights Found`
    },
    skeletonCards() {
      return [1, 2, 3, 4]
    },
    skeletonSettings() {
      return {
        ...this.carouselSettings,
        arrows: false,
        draggable: false,
        swipe: false,
        touchMove: false,
      }
    },
    previewsSettled() {
      if (!this.activeVideoUrl) return false
      const highlights = Array.isArray(this.highlights) ? this.highlights : []
      if (!highlights.length) return false

      return highlights.every(highlight => {
        const key = this.thumbnailKey(highlight)
        return Boolean(this.previewReadyMap[key] || this.previewErrorMap[key])
      })
    },
  },
  watch: {
    highlights: {
      deep: true,
      handler() {
        this.resetPreviewState()
      },
    },
    activeVideoUrl() {
      this.resetPreviewState()
    },
  },
  beforeDestroy() {
    this.clearPreviewTimers()
    if (this.wheelResetTimer) {
      clearTimeout(this.wheelResetTimer)
      this.wheelResetTimer = null
    }
  },
  methods: {
    highlightEventId(highlight) {
      const sceneId = String(
        highlight?.scene_id ??
        highlight?.best_key ??
        highlight?.id ??
        ''
      ).trim()
      if (sceneId) return `highlight-${sceneId}`

      const timestamp = this.highlightTimestamp(highlight)
      const sourceHighlightId = String(highlight?.highlight_id || '').trim() || 'highlight'
      return Number.isFinite(timestamp) ? `highlight-${sourceHighlightId}-${timestamp}` : ''
    },
    highlightStartLabel(highlight) {
      const startSecond = this.highlightTimestamp(highlight)
      return Number.isFinite(startSecond) ? this.formatDuration(startSecond) : 'n/a'
    },
    highlightTimestamp(highlight) {
      const timestamp = Number(
        highlight?.best_frame_timestamp_sec ??
        highlight?.best_match_timestamp_sec ??
        highlight?.start_second
      )
      return Number.isFinite(timestamp) ? timestamp : null
    },
    thumbnailKey(highlight) {
      return this.highlightEventId(highlight) || String(highlight?.id || '')
    },
    highlightDurationSeconds(highlight) {
      const explicitDuration = Number(highlight?.duration_seconds)
      if (Number.isFinite(explicitDuration) && explicitDuration > 0) return explicitDuration

      const startSecond = Number(highlight?.start_second)
      const endSecond = Number(highlight?.end_second)
      if (Number.isFinite(startSecond) && Number.isFinite(endSecond) && endSecond > startSecond) {
        return endSecond - startSecond
      }

      return 1
    },
    highlightDurationLabel(highlight) {
      return this.formatDuration(this.highlightDurationSeconds(highlight))
    },
    previewElement(highlight) {
      const ref = this.$refs[`preview-${this.thumbnailKey(highlight)}`]
      return Array.isArray(ref) ? ref[0] || null : ref || null
    },
    showPreviewFallback(highlight) {
      const key = this.thumbnailKey(highlight)
      if (!this.activeVideoUrl) return true
      return Boolean(this.previewErrorMap[key])
    },
    safePreviewTime(video, timestamp) {
      const duration = Number(video?.duration)
      return Number.isFinite(duration) && duration > 0
        ? Math.min(Math.max(timestamp, 0), Math.max(0, duration - 0.1))
        : Math.max(timestamp, 0)
    },
    preparePreview(highlight) {
      const video = this.previewElement(highlight)
      const timestamp = this.highlightTimestamp(highlight)
      if (!video || !Number.isFinite(timestamp)) return

      video.currentTime = this.safePreviewTime(video, timestamp)
    },
    markPreviewReady(highlight) {
      const key = this.thumbnailKey(highlight)
      this.$set(this.previewReadyMap, key, true)
      this.$delete(this.previewErrorMap, key)
    },
    markPreviewError(highlight) {
      const key = this.thumbnailKey(highlight)
      this.$set(this.previewErrorMap, key, true)
      this.$delete(this.previewReadyMap, key)
    },
    clearPreviewTimer(highlight) {
      const key = this.thumbnailKey(highlight)
      if (this.previewTimers[key]) {
        clearTimeout(this.previewTimers[key])
        this.$delete(this.previewTimers, key)
      }
    },
    clearPreviewTimers() {
      Object.keys(this.previewTimers).forEach(key => {
        clearTimeout(this.previewTimers[key])
      })
      this.previewTimers = {}
    },
    resetPreviewState() {
      this.clearPreviewTimers()
      this.previewReadyMap = {}
      this.previewErrorMap = {}
      if (this.wheelResetTimer) {
        clearTimeout(this.wheelResetTimer)
        this.wheelResetTimer = null
      }
      this.lastWheelDirection = 0
    },
    handleWheelScroll(event) {
      const carousel = this.$refs.highlightsCarousel
      if (!carousel) return

      const primaryDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY
      if (Math.abs(primaryDelta) < 8) return

      const direction = primaryDelta > 0 ? 1 : -1
      if (this.lastWheelDirection === direction) return

      this.lastWheelDirection = direction
      if (direction > 0) {
        carousel.next()
      } else {
        carousel.prev()
      }

      clearTimeout(this.wheelResetTimer)
      this.wheelResetTimer = setTimeout(() => {
        this.lastWheelDirection = 0
      }, 140)
    },
    async startPreview(highlight) {
      const video = this.previewElement(highlight)
      const key = this.thumbnailKey(highlight)
      const timestamp = this.highlightTimestamp(highlight)
      if (!video || !Number.isFinite(timestamp) || this.previewErrorMap[key]) return
      if (video.readyState < 1) return

      this.clearPreviewTimer(highlight)

      try {
        const safeTime = this.safePreviewTime(video, timestamp)
        const needsSeek = Math.abs(Number(video.currentTime || 0) - safeTime) > 0.35
        if (needsSeek) {
          video.currentTime = safeTime
        }
        const playPromise = video.play()
        if (playPromise?.catch) {
          await playPromise.catch(() => {
            this.markPreviewError(highlight)
          })
        }
      } catch (_) {
        this.markPreviewError(highlight)
        return
      }

      const durationMs = Math.max(250, this.highlightDurationSeconds(highlight) * 1000)
      this.$set(this.previewTimers, key, setTimeout(() => {
        this.stopPreview(highlight)
      }, durationMs))
    },
    stopPreview(highlight) {
      const video = this.previewElement(highlight)
      const timestamp = this.highlightTimestamp(highlight)
      this.clearPreviewTimer(highlight)
      if (!video) return

      video.pause()
      if (Number.isFinite(timestamp)) {
        try {
          video.currentTime = this.safePreviewTime(video, timestamp)
        } catch (_) {
          // Ignore failed seeks while metadata is unavailable.
        }
      }
    },
  },
}
</script>

<style scoped>
.highlights-shell {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 2px;
}

.highlights-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  overflow: visible;
}

.highlights-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.result-count {
  border-radius: 999px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 6px 10px;
  white-space: nowrap;
}

.highlights-carousel-shell {
  visibility: hidden;
}

.highlights-skeleton-shell {
  visibility: visible;
}

.highlights-carousel-ready {
  visibility: visible;
}

.highlights-carousel {
  padding: 0 2px 8px;
}

.highlights-carousel :deep(.slick-list) {
  margin: 0 -8px;
}

.highlights-carousel :deep(.slick-slide) {
  height: inherit;
}

.highlights-carousel :deep(.slick-slide > div) {
  height: 100%;
  padding: 0 8px;
}

.highlights-carousel :deep(.slick-disabled) {
  opacity: 0.35;
}

.highlights-carousel :deep(.slick-prev),
.highlights-carousel :deep(.slick-next) {
  z-index: 2;
  width: 32px;
  height: 32px;
  border-radius: 999px;
}

.highlights-carousel :deep(.slick-prev) {
  left: -48px;
}

.highlights-carousel :deep(.slick-next) {
  right: -48px;
}

.highlights-carousel :deep(.slick-prev::before),
.highlights-carousel :deep(.slick-next::before) {
  color: #0f172a;
  font-size: 32px;
  opacity: 1;
}

.highlight-card {
  display: block;
  width: 100%;
  padding: 0;
  border: 1px solid #d1d5db;
  text-align: left;
  line-height: 0;
  border-radius: 0;
  overflow: hidden;
  background: #ffffff;
  appearance: none;
  -webkit-appearance: none;
  transition: border-color 0.2s ease;
  border-radius: 0.25rem;
}

.highlight-card-active {
  border-color: #3730a3;
}

.highlight-card-skeleton {
  border-color: #d6dbe5;
  background: #fff;
  cursor: default;
}

.highlight-thumbnail-shell {
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  line-height: 0;
  background: #000;
  overflow: hidden;
  border-radius: 0.25rem;
}

.highlight-thumbnail-shell-skeleton {
  background: #fff;
}

.highlight-preview {
  position: absolute;
  inset: 0;
  width: 100%;
  height: auto;
  display: block;
  object-fit: cover;
  border: 0;
  outline: 0;
  background: #000;
}

.highlight-duration-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 8px;
  border-radius: 0.25rem;
  background: #000;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
}

.highlight-play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  pointer-events: none;
  opacity: 1;
  transition: opacity 0.18s ease;
}

.highlight-play-icon {
  width: 96px;
  height: 96px;
  display: block;
}

.highlight-card:hover .highlight-play-overlay,
.highlight-card:focus .highlight-play-overlay,
.highlight-card:focus-visible .highlight-play-overlay {
  opacity: 0;
}

.highlight-thumbnail-fallback {
  position: absolute;
  inset: 0;
  width: 100%;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  background: #000;
  z-index: 2;
}

.highlight-fallback-time {
  font-size: 0.85rem;
  font-weight: 600;
}

.highlight-skeleton-block {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, #e2e8f0 0%, #f8fafc 50%, #e2e8f0 100%);
  background-size: 200% 100%;
  animation: highlight-skeleton-shimmer 1.3s ease-in-out infinite;
}

@keyframes highlight-skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }

  100% {
    background-position: -200% 0;
  }
}

@media (max-width: 767px) {
  .section-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .highlights-carousel {
    padding-inline: 0;
  }

  .highlights-carousel :deep(.slick-list) {
    margin: 0;
  }

  .highlights-carousel :deep(.slick-slide > div) {
    padding: 0;
  }

  .highlights-carousel :deep(.slick-prev) {
    left: 8px;
  }

  .highlights-carousel :deep(.slick-next) {
    right: 8px;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .highlights-carousel :deep(.slick-prev) {
    left: -8px;
  }

  .highlights-carousel :deep(.slick-next) {
    right: -8px;
  }
  .highlights-carousel :deep(.slick-prev::before),
  .highlights-carousel :deep(.slick-next::before) {
    color: #fff;
  }
}
</style>
